import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { useTheme } from '@cobalt/react-theme-provider';
import Flex, { FlexProps } from '@cobalt/react-flex';
import Box from '@cobalt/react-box';
import Tab from '@cobalt/react-tab';
import Divider from '@cobalt/react-divider';

import TabPane from './TabPane';

export interface TabsProps {
  prefixCls?: string;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  tabNavProps?: FlexProps;
  tabPaneStyle?: React.CSSProperties;
  children?: React.ReactNode;

  activeKey?: string;
  defaultActiveKey?: string;
  size?: 'small' | 'medium';
  divider?: boolean;

  onChange?: (activeKey: string) => void;
  onTabClick?: (activeKey: string, e: React.MouseEvent) => void;
}

function Tabs(props: TabsProps) {
  const {
    prefixCls = 'cobalt',
    id,
    className,
    style,
    tabNavProps,
    tabPaneStyle,
    children,
    activeKey,
    defaultActiveKey,
    size = 'medium',
    divider = false,
    onChange,
    onTabClick,
  } = props;

  const componentCls = `${prefixCls}-tabs`;
  const mergeClassNames = classNames(`${componentCls}`, className);

  const theme = useTheme();

  const [curKey, setCurKey] = useState(defaultActiveKey);

  const handleClick = useCallback(
    (activeKey: string, e: React.MouseEvent) => {
      onTabClick?.(activeKey, e);
      activeKey !== curKey && onChange?.(activeKey);
    },
    [curKey, onChange, onTabClick],
  );

  const tabNavList = useMemo(() => {
    let result: React.ReactNode[] = [];

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child) || !child.key) return;
      if (typeof child.type === 'string') return;
      const type = child.type as typeof child.type & { displayName: string };
      if (type.displayName !== 'TabPane') return;

      result.push(
        <Box key={child.key} paddingRight="4">
          <Tab
            data-bi={child.props.dataPrefix}
            active={child.key === curKey}
            disabled={child.props.disabled}
            size={size}
            forwardedRef={child.props.forwardedRef}
            onClick={(e) => {
              if (child.props.disabled) return;
              handleClick(child.key as string, e);
            }}
          >
            {child.props.tab}
          </Tab>
        </Box>,
      );
    });

    return result;
  }, [children, curKey, handleClick, size]);

  const tabPaneList = useMemo(() => {
    let result: React.ReactNode[] = [];

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child) || !child.key) return;
      if (typeof child.type === 'string') return;
      const type = child.type as typeof child.type & { displayName: string };
      if (type.displayName !== 'TabPane') return;

      result.push(
        React.cloneElement(child, {
          prefixCls,
          style: {
            display: child.key === curKey ? 'block' : 'none',
            ...child.props.style,
          },
          tabKey: child.key,
        } as any),
      );
    });

    return result;
  }, [children, curKey, prefixCls]);

  const tabPanelHeight = useMemo(() => {
    return `calc(100% - 40px - ${divider ? '1px' : '0px'})`;
  }, [divider]);

  useEffect(() => {
    setCurKey(activeKey || defaultActiveKey);
  }, [activeKey, defaultActiveKey]);

  return (
    <Box style={style} id={id} className={mergeClassNames} height="100%">
      <Flex backgroundColor={theme.white} {...tabNavProps}>
        {tabNavList}
      </Flex>

      {divider && <Divider />}

      <Box style={{ ...tabPaneStyle, position: 'relative' }} height={tabPanelHeight} scrollable>
        {tabPaneList}
      </Box>
    </Box>
  );
}

Tabs.TabPane = TabPane;
Tabs.displayName = 'Tabs';

export default Tabs;
